import React, { useState } from 'react';
import { Button } from '@rsos/base-components';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useGetConversation } from '@rsos/reduxDataStore/src/features/conversations/utils';
import {
  mockTcapiAgencyId,
  simulateTcapiCallMissed,
  simulateTcapiTransferFailure,
} from '../services/els';
import {
  StyledDescription,
  StyledError,
  StyledSuccess,
  StyledWarning,
} from './TextTo911DebugPanel.styles';

const TRANSFER_STATUSES = ['pending', 'accepted', 'timed_out'] as const;

interface MockTransferControlsProps {
  callerID: string;
}

/**
 * Creates and dispatches a mock conversation.transfer message on the StreamWS
 * handle with the given callerID and transfer status. This is used to simulate
 * incoming transfer messages for testing the frontend's handling of different
 * transfer statuses. This is a **frontend-only mock and will not be reflected
 * on the backend.**
 */
function dispatchMockTransfer(
  callerID: string,
  currentUserAuthorityId: string | undefined,
  transferStatus: (typeof TRANSFER_STATUSES)[number],
) {
  const handle = window.__RSOS?.streamWS?.handle;

  if (!handle) {
    // eslint-disable-next-line no-console -- debug panel
    console.warn('StreamWS handle not available');
    return;
  }

  handle({
    type:
      transferStatus == 'pending'
        ? 'conversation.transfer'
        : 'conversation.transfer.update',
    // Required by normalizeMessage in StreamWS.js which checks
    // topic.startsWith('tel://') — empty string passes through unchanged.
    topic: '',
    body: {
      caller_id: callerID,
      conversation_id: `mock-transfer-${Date.now()}`,
      transfer_status: transferStatus,
      receiving_authority_display_name: 'Mock Agency',
      receiving_authority_account_id: 'mock-agency-account-id',
      initiating_authority_display_name: 'Mock Initiating Agency',
      initiating_authority_account_id: currentUserAuthorityId,
      source: 'tcapi',
      application: 'text_to_911',
      created_time: Date.now(),
      conversation_start_time: Date.now(),
      valid_for_psap: true,
    },
    source: 'tcapi',
  });
}

/**
 * Dispatches a mock conversation.transfer message simulating an incoming
 * transfer where the current user's agency is the **receiving** agency.
 * Uses a fake caller ID to avoid collisions with existing conversations.
 */
function dispatchMockIncomingTransfer(
  currentUserAuthorityId: string,
  currentUserAuthorityDisplayName: string,
) {
  const handle = window.__RSOS?.streamWS?.handle;

  if (!handle) {
    // eslint-disable-next-line no-console -- debug panel
    console.warn('StreamWS handle not available');
    return;
  }

  const mockCallerID = `1555${String(Date.now()).slice(-7)}`;

  handle({
    type: 'conversation.transfer',
    topic: '',
    body: {
      caller_id: mockCallerID,
      conversation_id: null,
      transfer_status: 'pending',
      receiving_authority_display_name: currentUserAuthorityDisplayName,
      receiving_authority_account_id: currentUserAuthorityId,
      initiating_authority_display_name: 'Sunnyvale ECC',
      initiating_authority_account_id: 'mock-initiating-account-id',
      source: 'tcapi',
      application: 'text_to_911',
      created_time: Date.now(),
      conversation_start_time: Date.now(),
      valid_for_psap: true,
    },
    source: 'tcapi',
  });
}

type CallMissedStatus = 'idle' | 'sending' | 'sent' | 'error';

interface BackendCallMissedControlProps {
  callerID: string;
  receivingAuthorityAccountId: string | null | undefined;
  receivingAuthorityDisplayName: string | null | undefined;
  transferStatus: string | undefined;
}

/**
 * Triggers Shanghai's mock TCAPI server to emit a real ``callMissed`` event
 * for the receiver's conference leg of the current soft transfer. Unlike the
 * frontend-only ``timed_out`` button above, this goes through the actual
 * backend timeout path (``handle_missed_soft_transfer`` →
 * ``TextSessionTransfer.status = timed_out``).
 *
 * Only works on devbox (where Shanghai mock is reachable and the receiving
 * agency's TCAPI agency ID follows the ``${accountId}-tcapi-agency``
 * convention from ``mockTcapiAgencyId``).
 */
function BackendCallMissedControl({
  callerID,
  receivingAuthorityAccountId,
  receivingAuthorityDisplayName,
  transferStatus,
}: BackendCallMissedControlProps) {
  const [status, setStatus] = useState<CallMissedStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const canTrigger =
    transferStatus === 'pending' && !!receivingAuthorityAccountId;

  async function handleClick() {
    if (!receivingAuthorityAccountId) return;
    setStatus('sending');
    setErrorMessage('');
    try {
      await simulateTcapiCallMissed(
        callerID,
        mockTcapiAgencyId(receivingAuthorityAccountId),
      );
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unknown error occurred',
      );
    }
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <StyledDescription>
        Trigger a real backend <code>callMissed</code> for the receiver&apos;s
        conference leg of the in-progress transfer (Shanghai-only). Routes
        through <code>handle_missed_soft_transfer</code> and resolves the
        transfer to <code>timed_out</code> the same way InDigital&apos;s
        &quot;Conversation missed&quot; does.
      </StyledDescription>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Button
          onClick={handleClick}
          disabled={!canTrigger || status === 'sending'}
          btnType="primary"
        >
          <strong>
            {status === 'sending'
              ? 'Sending...'
              : 'Send backend callMissed (receiver leg)'}
          </strong>
        </Button>
        {receivingAuthorityDisplayName && (
          <span style={{ fontSize: '12px', opacity: 0.7 }}>
            → {receivingAuthorityDisplayName}
          </span>
        )}
      </div>
      {!canTrigger && (
        <StyledDescription style={{ marginTop: '8px' }}>
          Available only while a transfer is <code>pending</code> for the
          selected call.
        </StyledDescription>
      )}
      {status === 'sent' && (
        <StyledSuccess>
          callMissed dispatched. The transfer should resolve to{' '}
          <code>timed_out</code> shortly.
        </StyledSuccess>
      )}
      {status === 'error' && <StyledError>Error: {errorMessage}</StyledError>}
    </div>
  );
}

// The mock blind-transfer target: Shanghai's hardcoded "agency-transfer-1"
// directory entry (retyped from "conference" to "transfer"). There is
// exactly one such mock agency, so this is a fixed literal rather than a
// picker.
const BLIND_TRANSFER_MOCK_AGENCY_ID = 'agency-transfer-1';

type TransferFailureStatus = 'idle' | 'sending' | 'armed' | 'error';

/**
 * Arms Shanghai's mock TCAPI server to fail the next ``transferCall`` (blind/
 * hard transfer) targeting the mock transfer agency, so the error path and
 * the "Transfer to Agency" modal's error banner can be exercised end-to-end
 * on devbox. Uses ``simulateTcapiTransferFailure``'s defaults — statusCode
 * 500, count 1 — which surfaces immediately with no wait, since 500 isn't
 * one of the codes text_to_911's WebSocketClient retries (502/503/504).
 *
 * Unlike ``BackendCallMissedControl``, this doesn't read anything off an
 * in-flight transfer — blind transfer has no ``pending`` state to read from
 * before success, since its ``TextSessionTransfer`` row is only created
 * *after* a successful transfer. So the control is always enabled: it arms
 * whichever blind transfer happens next.
 */
function BlindTransferFailureControl() {
  const [status, setStatus] = useState<TransferFailureStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleClick() {
    setStatus('sending');
    setErrorMessage('');
    try {
      await simulateTcapiTransferFailure(BLIND_TRANSFER_MOCK_AGENCY_ID);
      setStatus('armed');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unknown error occurred',
      );
    }
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <StyledDescription>
        Force the next blind transfer ("Transfer to Agency" →{' '}
        <code>{BLIND_TRANSFER_MOCK_AGENCY_ID}</code>) to fail immediately, so
        the modal's error banner can be verified end-to-end (Shanghai-only).
      </StyledDescription>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Button
          onClick={handleClick}
          disabled={status === 'sending'}
          btnType="primary"
        >
          <strong>
            {status === 'sending'
              ? 'Arming...'
              : 'Force next blind transfer to fail'}
          </strong>
        </Button>
      </div>
      {status === 'armed' && (
        <StyledSuccess>
          Next <code>transferCall</code> to {BLIND_TRANSFER_MOCK_AGENCY_ID} will
          fail immediately.
        </StyledSuccess>
      )}
      {status === 'error' && <StyledError>Error: {errorMessage}</StyledError>}
    </div>
  );
}

export function MockTransferControls({ callerID }: MockTransferControlsProps) {
  const currentUserAuthority = useCurrentPSAP();
  const currentUserAuthorityId = currentUserAuthority?.account_id;
  const currentUserAuthorityDisplayName =
    currentUserAuthority?.display_name ?? 'Unknown Agency';
  const conversation = useGetConversation(callerID);
  const transfer = conversation?.transfer;

  if (!currentUserAuthorityId) {
    throw new Error(
      "Current user's authority account ID is required for MockTransferControls",
    );
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <StyledDescription>
        Mock a conversation transfer status (initiating side):
      </StyledDescription>
      <StyledWarning>
        Frontend-only mock. This will not be reflected on the backend.
      </StyledWarning>
      <div style={{ display: 'flex', gap: '8px' }}>
        {TRANSFER_STATUSES.map(status => (
          <Button
            key={status}
            onClick={() =>
              dispatchMockTransfer(callerID, currentUserAuthorityId, status)
            }
            btnType="secondary"
          >
            <strong>{status}</strong>
          </Button>
        ))}
      </div>
      <StyledDescription style={{ marginTop: '12px' }}>
        Mock an incoming transfer (receiving side):
      </StyledDescription>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          onClick={() =>
            dispatchMockIncomingTransfer(
              currentUserAuthorityId,
              currentUserAuthorityDisplayName,
            )
          }
          btnType="secondary"
        >
          <strong>Receive Transfer</strong>
        </Button>
      </div>
      <BackendCallMissedControl
        callerID={callerID}
        receivingAuthorityAccountId={transfer?.receiving_authority_account_id}
        receivingAuthorityDisplayName={
          transfer?.receiving_authority_display_name
        }
        transferStatus={transfer?.status}
      />
      <BlindTransferFailureControl />
    </div>
  );
}
